import {
	importZipFiles,
	selectFolder,
	showSelectFolderErrorDialog,
	showSupportedFormatsDialog,
} from "@/store/project/project"
import { StyleRecord } from "@/storage/indexeddb"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import clsx from "clsx"
import { replaceDialog, resetDialogs } from "@/store/dialogs-state"
import { PermissionsDialog } from "./PermissionsDialog"
import { Dialog } from "../ui/Dialog"
import { Button } from "../ui/Button"
import { createProject, NewStyleSetupOptions } from "@/store/project/create-project"
import hdPngUrl from "@assets/hd.png?url"
import folderSelectionGuidanceUrl from "@assets/folder_selection_guidance.png?url"
import { analytics } from "@/telemetry/amplitude"
import { getFileExtension } from "@/utils/file"
import { checkDirectoryIsValid } from "@/storage/open-directory"
import { isZipfileIOEnabled, getOpfsImportDir } from "@/utils/zipfile-io"
import { StyleResponseDTO } from "@/api/dto/styles"

export interface SelectFolderDialogProps {
	showFolderSelectionGuidance: boolean
	selectedStyle?: StyleRecord
	selectedStyleDTO?: StyleResponseDTO
	newStyleSetup?: NewStyleSetupOptions
}

export const SelectFolderDialog: React.FC<SelectFolderDialogProps> = ({
	showFolderSelectionGuidance,
	selectedStyle,
	selectedStyleDTO,
	newStyleSetup,
}) => {
	const { t } = useTranslation("components")
	const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
		setIsDragging(false)

		e.stopPropagation()
		e.preventDefault()

		const item = e.dataTransfer.items[0]
		const count = e.dataTransfer.items.length
		if (!item) return

		const handle = await item.getAsFileSystemHandle()

		const info = {
			kind: handle?.kind,
			format: handle?.name && getFileExtension(handle?.name),
			count: count,
			selectMethod: "drag_and_drop",
			type: "import_photo",
		}

		if (handle && handle.kind === "directory" && handle instanceof FileSystemDirectoryHandle) {
			replaceDialog(PermissionsDialog)
			const result = await checkDirectoryIsValid(handle)
			resetDialogs()
			if (result instanceof FileSystemDirectoryHandle) {
				createProject(result, selectedStyle ?? null, selectedStyleDTO, newStyleSetup)
				analytics.trackEvent({
					label: "folder_selected",
					type: "event",
					customProperties: info,
				})
			} else {
				analytics.trackEvent({
					label: "folder_select_error",
					type: "error",
					customProperties: info,
					errorReason: result,
				})
			}
		} else {
			analytics.trackEvent({
				label: "folder_select_error",
				type: "error",
				customProperties: info,
				errorReason: "not_a_folder",
			})
			showSelectFolderErrorDialog({
				selectedStyle,
				selectedStyleDTO,
				newStyleSetup,
			})
		}
	}

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(true)
		const item = e.dataTransfer.items[0]
		if (!item) {
			return
		}
	}

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(false)
	}

	const [isDragging, setIsDragging] = useState(false)

	return (
		/*
			max-h-[min(720px,calc(100vh-2rem))]: 
				This is to allow the layout engine to expand the height of the dialog at very low resolutions, thus avoiding component overlap.
				leaves 2rem total vertical margin
		*/
		<Dialog
			contentSize="md"
			trackingViewName="select_folder_dialog"
			contentProps={{
				className: "max-h-[min(720px,calc(100vh-2rem))] min-h-0 overflow-y-auto",
			}}
		>
			<div
				className="flex h-full min-h-0 flex-col justify-between space-y-4"
				data-allow-file-drop
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				onDragLeave={handleDragLeave}
			>
				<div className="text-start text-md font-bold">{t("selectFolderDialog.title")}</div>
				<div className="text-start text-sm text-base-400">
					{t("selectFolderDialog.description")}
				</div>
				<div
					className={clsx(
						isDragging ? "bg-base-400/30" : "bg-base-400/10",
						"flex min-h-0 flex-1 flex-col items-center justify-between space-y-2 rounded-md border border-dashed border-base-400 py-2",
					)}
				>
					<div className="flex min-h-0 flex-1 flex-col items-center justify-center space-y-2">
						<img
							className="h-[clamp(56px,14vh,120px)] w-auto max-w-full self-center object-contain"
							src={
								showFolderSelectionGuidance ? folderSelectionGuidanceUrl : hdPngUrl
							}
						/>

						<div
							className={clsx("text-xs", showFolderSelectionGuidance && "font-bold")}
						>
							{t("selectFolderDialog.dropFolderHere")}
						</div>
						{showFolderSelectionGuidance && (
							<div className="text-xs">
								{t("selectFolderDialog.cannotAddIndividualPhotos")}
							</div>
						)}
						<div className="flex flex-row items-center space-x-1 text-xs text-base-400">
							<div className="h-px w-10 bg-base-700"></div>
							<div>{t("common:label.or")}</div>
							<div className="h-px w-10 bg-base-700"></div>
						</div>
						<Button
							trackingLabel="select_folder"
							aria-label={t("selectFolderDialog.ariaSelectProjectFolder")}
							size="sm"
							variant="primary"
							onClick={() => {
								void selectFolder(
									selectedStyle ?? null,
									selectedStyleDTO,
									newStyleSetup,
								)
							}}
						>
							{t("selectFolderDialog.selectFolder")}
						</Button>

						{isZipfileIOEnabled() && (
							<Button
								trackingLabel="import_project_zipfile"
								aria-label={t("selectFolderDialog.ariaImportProjectFromZip")}
								size="sm"
								variant="primary"
								onClick={() => {
									importZipFiles({
										style: selectedStyle ?? undefined,
									})
								}}
							>
								{t("selectFolderDialog.importZipFile")}
							</Button>
						)}

						{isZipfileIOEnabled() && (
							<Button
								trackingLabel="import_project_opfs"
								aria-label={t("selectFolderDialog.ariaImportProjectFromOpfs")}
								size="sm"
								variant="primary"
								onClick={async () => {
									const opfsRoot = await navigator.storage.getDirectory()
									const dirName = getOpfsImportDir()
									const handle = await opfsRoot.getDirectoryHandle(dirName)
									createProject(
										handle,
										selectedStyle ?? null,
										selectedStyleDTO,
										newStyleSetup,
									)
								}}
							>
								{t("selectFolderDialog.useOpfsEntry")}
							</Button>
						)}
					</div>
					<Button
						className="text-base-400"
						trackingLabel="view_supported_formats"
						aria-label={t("selectFolderDialog.ariaViewSupportedFileFormats")}
						variant="ghost"
						onClick={() => showSupportedFormatsDialog()}
					>
						{t("selectFolderDialog.viewSupportedFormats")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
